// ── Email send via Supabase Edge Function ────────────────────────────────────
// The Edge Function "send-safety-email" (multi-tenant) mails the signed PDF from
// a per-org address (fromFor(orgName)) to the org's configured safety recipients
// (org_notification_recipients event 'safety_form', fallback org admins) plus the
// optional per-project CSO. The org is resolved from the caller's JWT, never the
// request body.

async function checkBackendHealth() {
  // Edge Functions don't need a wake-up call. Just confirm the browser has
  // network connectivity — the actual invoke will surface any real failure.
  return navigator.onLine !== false;
}

// PDF buffer → base64 string (no data: prefix). FileReader is used so very
// large PDFs don't blow the call stack (which String.fromCharCode would).
function pdfBufferToBase64(pdfBuffer) {
  const blob = new Blob([pdfBuffer], { type: 'application/pdf' });
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload  = () => resolve(String(reader.result).split(',')[1]);
    reader.onerror = () => reject(new Error('Failed to encode PDF'));
    reader.readAsDataURL(blob);
  });
}

async function submitForm(formData, pdfBuffer, photos, submissionId) {
  // ── submission_id mode (2026-09-01): the row is filed, so the server owns
  // the send — it reads the PDF from storage, resolves safety inbox + CSO +
  // Salus itself, and stamps the outcome on the row. The request carries an
  // id instead of a multi-MB base64 PDF, so it is fast and retry-safe.
  let payload;
  if (submissionId) {
    payload = { submission_id: submissionId };
  } else {
    // Legacy mode — no row landed (or an old cached client): PDF in the body.
    const pdfBase64   = await pdfBufferToBase64(pdfBuffer);
    const pdfFilename = buildFileName(formData);
    payload = {
      pdfBase64,
      pdfFilename,
      foremanName:   formData.foremanName,
      projectName:   formData.project,
      formType:      formData.submissionType,
      date:          formData.date,
      workersOnSite: formData.workersOnSite || '',
    };
    if (formData.sendToSafety && formData._csoContact?.cso_email) {
      payload.csoEmail = formData._csoContact.cso_email;
      payload.csoName  = formData._csoContact.cso_name || '';
    }
  }

  // Ask auth for the CURRENT session first: getSession() refreshes an expired
  // token, and the explicit header overrides whatever (possibly stale) token
  // the functions client cached. Without this, a PWA left open overnight
  // inserted rows fine (REST resolves the token per request) while every
  // send-safety-email call 401'd — the foreman saw "failed", retried, and
  // each retry filed another signed copy (2026-08-28).
  let _authHeaders;
  try {
    const { data: sess } = await sbClient.auth.getSession();
    if (sess?.session?.access_token) _authHeaders = { Authorization: `Bearer ${sess.session.access_token}` };
  } catch (_) {}
  const { data, error } = await sbClient.functions.invoke('send-safety-email', {
    body: payload,
    ...(_authHeaders ? { headers: _authHeaders } : {}),
  });

  if (error) {
    // Supabase wraps non-2xx responses in FunctionsHttpError. Try to pull the
    // server's JSON error out of context if available.
    let detail = error.message || 'Edge Function error';
    try {
      if (error.context && typeof error.context.json === 'function') {
        const body = await error.context.json();
        detail = body?.details || body?.error || detail;
      }
    } catch {}
    throw new Error(detail);
  }

  if (data?.error) {
    throw new Error(typeof data.error === 'string' ? data.error : 'Send failed');
  }

  // Shape the response so the existing success screen (app.js → showSuccessScreen)
  // continues to work without changes. The success screen reads `message` and
  // `filesAttached` from the result object.
  const recipients = Array.isArray(data?.recipients) ? data.recipients : [];
  const salusNote  = data?.salus?.sent ? ' (+ Salus Pro)' : '';
  return {
    message:       `Sent to ${recipients.length || 1} recipient(s)${salusNote}`,
    filesAttached: 1,
    recipients,
    salus:         data?.salus || { sent: false },
    from:          data?.from || '',
  };
}
